import type { Video, VideoProcessingState } from '@/models';

export interface VideoProcessingStatus {
  showUploadButton: boolean;
  showProcessingStates: boolean;
  showErrorAlert: boolean;
  canSetPublished: boolean;
  errorMessage: string | null;
  processingMessage: string | null;
}

export function getVideoProcessingStatus(video: Video | undefined | null): VideoProcessingStatus {
  if (!video) {
    return {
      showUploadButton: false,
      showProcessingStates: false,
      showErrorAlert: false,
      canSetPublished: false,
      errorMessage: null,
      processingMessage: null
    };
  }

  const states = [
    video.localProcessingState,
    video.cdnUploadState,
    video.cdnProcessingState
  ];
  
  const errors = [
    video.localProcessingError,
    video.cdnUploadError,
    video.cdnProcessingError
  ].filter(Boolean);

  // Check if any state has an error
  const hasError = states.includes('ERROR' as VideoProcessingState);
  const allWaiting = states.every(state => state === 'WAITING');
  const allDone = states.every(state => state === 'DONE');

  // Show upload button if all states are WAITING
  const showUploadButton = allWaiting;

  // Show processing states only if video was uploaded (not all waiting) and not all done
  const showProcessingStates = !allWaiting && !allDone;

  // Show error alert if there are errors
  const showErrorAlert = hasError;

  // Can only set published if all states are DONE
  const canSetPublished = allDone;

  // Get error message
  const errorMessage = errors.length > 0 ? errors.join('; ') : null;

  // Get processing message
  let processingMessage = null;
  if (showProcessingStates) {
    const messages = [];
    if (video.localProcessingState === 'QUEUED') {
      messages.push('Lokale Verarbeitung in Warteschlange...');
    } else if (video.localProcessingState === 'ACTIVE') {
      messages.push('Lokale Verarbeitung läuft...');
    } else if (video.localProcessingState === 'DONE') {
      messages.push('Lokale Verarbeitung abgeschlossen');
    }
    
    if (video.cdnUploadState === 'QUEUED') {
      messages.push('Upload zum CDN in Warteschlange...');
    } else if (video.cdnUploadState === 'ACTIVE') {
      messages.push('Upload zum CDN läuft...');
    } else if (video.cdnUploadState === 'DONE') {
      messages.push('CDN Upload abgeschlossen');
    }
    
    if (video.cdnProcessingState === 'QUEUED') {
      messages.push('CDN Verarbeitung in Warteschlange...');
    } else if (video.cdnProcessingState === 'ACTIVE') {
      messages.push('CDN Verarbeitung läuft...');
    } else if (video.cdnProcessingState === 'DONE') {
      messages.push('CDN Verarbeitung abgeschlossen');
    }
    
    processingMessage = messages.join(' • ');
  }

  return {
    showUploadButton,
    showProcessingStates,
    showErrorAlert,
    canSetPublished,
    errorMessage,
    processingMessage
  };
}

export function formatProcessingState(state: VideoProcessingState): string {
  switch (state) {
    case 'WAITING':
      return 'Wartend';
    case 'QUEUED':
      return 'In Warteschlange';
    case 'ACTIVE':
      return 'Aktiv';
    case 'DONE':
      return 'Fertig';
    case 'ERROR':
      return 'Fehler';
    default:
      return state;
  }
}

export function getProcessingStateColor(state: VideoProcessingState): string {
  switch (state) {
    case 'WAITING':
      return 'text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    case 'QUEUED':
      return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300';
    case 'ACTIVE':
      return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300';
    case 'DONE':
      return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
    case 'ERROR':
      return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300';
    default:
      return 'text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
  }
}

export function isVideoActivelyProcessing(video: Video | undefined | null): boolean {
  if (!video) return false;
  
  const states = [
    video.localProcessingState,
    video.cdnUploadState,
    video.cdnProcessingState
  ];
  
  // Video is actively processing if any state is ACTIVE or QUEUED
  return states.includes('ACTIVE' as VideoProcessingState) || states.includes('QUEUED' as VideoProcessingState);
}

export function isVideoReadyToPlay(video: Video | undefined | null): boolean {
  if (!video) return false;
  
  const processingStatus = getVideoProcessingStatus(video);
  // Video is ready to play when processing is complete and there are no errors
  return processingStatus.canSetPublished && !processingStatus.showErrorAlert;
}